import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Live updates for the single-thread chat inbox (dashboard/site_inbox#show).
// Subscribes to Sites::Chats::ManagerChannel for the website and appends
// incoming messages to the transcript as they arrive — so a manager with the
// thread open sees new visitor messages without refreshing.
//
// Manager replies are sent via a full-page POST (the composer form), so the
// page already reloads with them; the dedupe-by-id guard keeps a live echo of
// the manager's own reply from double-rendering in the rare race.
export default class extends Controller {
  static values = { websiteId: String, chatId: String }

  static targets = ["transcript", "empty"]

  connect() {
    if (!this.websiteIdValue) return
    this.subscription = consumer.subscriptions.create(
      { channel: "Sites::Chats::ManagerChannel", website_id: this.websiteIdValue },
      { received: (data) => this.handleEvent(data) }
    )
  }

  disconnect() {
    this.subscription?.unsubscribe()
    this.subscription = null
  }

  handleEvent(data) {
    if (!data || data.type !== "message") return
    if (data.chat_id !== this.chatIdValue) return
    const msg = data.message
    if (!msg || !msg.id) return
    if (!this.hasTranscriptTarget) return
    if (this.transcriptTarget.querySelector(`[data-message-id="${msg.id}"]`)) return

    if (this.hasEmptyTarget) this.emptyTarget.remove()
    this.transcriptTarget.appendChild(this.buildBubble(msg))
    this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight
  }

  buildBubble(msg) {
    const isVisitor = msg.sender_kind === "visitor"
    const isSystem = msg.sender_kind === "system"

    const wrap = document.createElement("div")
    wrap.className = `flex ${isVisitor ? "justify-start" : "justify-end"}`
    wrap.dataset.messageId = msg.id

    const inner = document.createElement("div")
    inner.className = "max-w-[78%]"

    const meta = document.createElement("div")
    meta.className = `text-[10px] uppercase tracking-wider text-gray-400 mb-1${isVisitor ? "" : " text-right"}`
    const who = isVisitor ? "Visitor" : isSystem ? "System" : (msg.sender_user?.name || "Team")
    meta.textContent = `${who} · just now${msg.source === "email" ? " · via email" : ""}`

    const bubble = document.createElement("div")
    bubble.className = isSystem ?
      "px-3 py-2 rounded-2xl text-sm italic text-gray-500" :
      `px-3 py-2 rounded-2xl text-sm ${isVisitor ? "bg-white border border-gray-200 text-gray-900 rounded-bl-sm" : "bg-gray-900 text-white rounded-br-sm"}`
    // textContent (not innerHTML) — message body is untrusted visitor input.
    bubble.textContent = msg.body || ""

    inner.appendChild(meta)
    inner.appendChild(bubble)
    wrap.appendChild(inner)
    return wrap
  }
}
